import { useCallback, useEffect, useRef, useState } from 'react';
import type { Order } from '@/types';

export type NewOrderNotification = {
  id: string;
  orderNumber: string;
  total: number;
  itemCount: number;
  receivedAt: number;
};

const DEFAULT_AUTO_DISMISS_MS = 12_000;

function resolveOrderTotal(order: Order): number {
  if (typeof order.total === 'number' && !Number.isNaN(order.total)) {
    return order.total;
  }
  return (order.subtotal || 0) + (order.deliveryFee || 0) + (order.serviceFee || 0);
}

export function useRestaurantNewOrderNotifications(options?: {
  autoDismissMs?: number;
}) {
  const autoDismissMs = options?.autoDismissMs ?? DEFAULT_AUTO_DISMISS_MS;
  const [notifications, setNotifications] = useState<NewOrderNotification[]>([]);
  const notifiedOrderIdsRef = useRef<Set<string>>(new Set());
  const dismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const timer = dismissTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      dismissTimersRef.current.delete(id);
    }
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    dismissTimersRef.current.forEach((timer) => clearTimeout(timer));
    dismissTimersRef.current.clear();
    setNotifications([]);
  }, []);

  const scheduleAutoDismiss = useCallback(
    (id: string) => {
      const timer = setTimeout(() => dismiss(id), autoDismissMs);
      dismissTimersRef.current.set(id, timer);
    },
    [autoDismissMs, dismiss],
  );

  const enqueueFromOrder = useCallback(
    (order: Order): boolean => {
      if (notifiedOrderIdsRef.current.has(order.id)) {
        return false;
      }

      notifiedOrderIdsRef.current.add(order.id);

      const notification: NewOrderNotification = {
        id: order.id,
        orderNumber: order.orderNumber || order.id.slice(0, 8),
        total: resolveOrderTotal(order),
        itemCount: order.items?.length ?? 0,
        receivedAt: Date.now(),
      };

      setNotifications((prev) => [...prev, notification]);
      scheduleAutoDismiss(order.id);
      return true;
    },
    [scheduleAutoDismiss],
  );

  useEffect(() => {
    const timers = dismissTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return {
    notifications,
    enqueueFromOrder,
    dismiss,
    dismissAll,
  };
}
